import { initializer, inject, injectable } from "@launchtray/tsyringe-async";
import axios, { AxiosResponse } from 'axios';
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { Event } from "event-system";
import { VariableSaver } from "../../tools/variableSaver/VariableSaver";
import { GuestAuthInfo } from "../scenes/common/models/GuestAuthInfo";
import { AuthConstants } from "../scenes/popups/login/AuthConstants";
import { INetworkClient } from "./INetworkClient";
import { INetworkConfig } from "./config/INetworkConfig";
import { CreateBluesnapWalletParamsDto } from "./data/requests/CreateBluesnapWalletParamsDto";
import { ProcessBluesnapOrderParamsDto } from "./data/requests/ProcessBluesnapOrderParamsDto";
import { PushSubscriptionData } from "./data/requests/PushSubscriptionData";
import { RefreshTokenInput } from "./data/requests/auth/RefreshTokenInput";
import { ProcessBluesnapCreditCardOrderDto } from "./data/requests/creditcard/ProcessBluesnapCreditCardOrderDto";
import { AddBuildingToMarketplaceDto } from "./data/requests/marketplace/AddBuildingToMarketplaceDto";
import { EditBuildingMarketlacePriceDto } from "./data/requests/marketplace/EditBuildingMarketlacePriceDto";
import { GetMarketplaceBuildingsDto } from "./data/requests/marketplace/GetMarketplaceBuildingsDto";
import { ClaimCurrentActionResultDto } from "./data/responses/ClaimCurrentActionResultDto";
import { GetCurrentGuestActionResultDto } from "./data/responses/GetCurrentGuestActionResultDto";
import { AuthTokensDto } from "./data/responses/auth/AuthTokensDto";
import { ClaimDailyWheelRewardResultDto } from "./data/responses/dailywheel/ClaimDailyWheelRewardResultDto";
import { DailyBonusWheelStatusDto } from "./data/responses/dailywheel/DailyBonusWheelStatusDto";
import { GoldenWheelStatusDto } from "./data/responses/dailywheel/goldenwheel/GoldenWheelStatusDto";
import { FirstTimeExperienceDto } from "./data/responses/firsttimeexperience/FirstTimeExperienceDto";
import { UpdateFirstTimeExperienceInputBody } from "./data/responses/firsttimeexperience/UpdateFirstTimeExperienceInputBody";
import { GetGuestBuildingsResultDto } from "./data/responses/guestbuildings/GetGuestBuildingsResultDto";
import { GuestProfileDto } from "./data/responses/guests/GuestProfileDto";
import { ClaimHourlyBonusResultDto } from "./data/responses/hourlybonus/ClaimHourlyBonusResultDto";
import { GetHourlyBonusesResultDto } from "./data/responses/hourlybonus/GetHourlyBonusesResultDto";
import { AddBuildingToMarketplaceResultDto } from "./data/responses/marketplace/buildings/AddBuildingToMarketplaceResultDto";
import { GetMarketplaceBuildingsResultDto } from "./data/responses/marketplace/buildings/GetMarketplaceBuildingsResultDto";
import { MarketplaceBuildingItemDto } from "./data/responses/marketplace/buildings/MarketplaceBuildingItemDto";
import { MarketplaceMapPointsDto } from "./data/responses/marketplace/mapPoints/MarketplaceMapPointsDto";
import { MarketplaceNewsDto } from "./data/responses/marketplace/news/MarketplaceNewsDto";
import { CreateBluesnapWalletResultDto } from "./data/responses/payments/CreateBluesnapWalletResultDto";
import { GetPersonalOfferDto } from "./data/responses/personaloffer/GetPersonalOfferDto";
import { PersonalOfferReason } from "./data/responses/personaloffer/PersonalOfferReason";
import { GetShopItemsDto } from "./data/responses/shop/GetShopItemsDto";
import { PlayerDto } from "./data/responses/user/PlayerDto";
import { BuildingUpgradesDto } from "./data/responses/upgrade/BuildingUpgradesDto";
import { UpgradeBuildingItemResultDto } from "./data/responses/upgrade/UpgradeBuildingItemResultDto";
import { FixBuildingItemResultDto } from "./data/responses/upgrade/FixBuildingItemResultDto";
import { ILogger } from "../logger/ILogger";
import { HttpClientWrapper } from "./HttpClientWrapper";
import { RenterPolicyStatusDto } from "./data/responses/renterPolicy/RenterPolicyStatusDto";
import { LeaderboardStatusDto } from "./data/responses/leaderboard/LeaderboardStatusDto";
import { LeaderboardClaimResonseDto } from "./data/responses/leaderboard/LeaderboardClaimResonseDto";
import { LatestNewsStatusDto } from "./data/responses/latestnews/LatestNewsStatusDto";
import { LatestNewsDto } from "./data/responses/latestnews/LatestNewsDto";
import { LatestNewsExpandedPopupInfoDto } from "./data/responses/latestnews/LatestNewsExpandedPopupInfoDto";
import { PiggyBankDto } from "./data/responses/piggybank/PiggyBankDto";
import { FixGuestBuildingItemInputBody } from "./data/responses/upgrade/FixGuestBuildingItemInputBody";
import { UpgradeGuestBuildingItemInputBody } from "./data/responses/upgrade/UpgradeGuestBuildingItemInputBody";
import { GuestBenefitsDto } from "./data/responses/benefits/GuestBenefitsDto";
import { PiggyWheelStatusDto } from "./data/responses/dailywheel/piggyWheel/PiggyWheelStatusDto";
import { ClaimPiggyWheelRewardResultDto } from "./data/responses/dailywheel/piggyWheel/ClaimPiggyWheelRewardResultDto";
import { AuthNonceDto } from "./data/responses/auth/web3/AuthNonceDto";
import { PrepareMintResultDto } from "./data/responses/marketplace/buildings/PrepareMintResultDto";
import { BurnNFTResultDTO } from "./data/responses/payments/BurnNFTResultDTO";
import { RewardDto } from "./data/responses/RewardDto";
import { MintRequestDto } from "./data/responses/MintRequestDto";
import { AuthTokenDisconnectionState } from "./ConnectionModel";
import { PersonalOfferBurnResultDto } from "./data/responses/personaloffer/PersonalOfferBurnResultDto";
import { UnklinkWalletDto } from "./data/responses/auth/web3/UnklinkWalletDto";
import { WalletsDto } from "./data/responses/auth/web3/WalletsDto";
import { RevealGiftCardResultDto } from "../scenes/game/flow/impl/diceActions/RevealGiftCardResultDto";
import { RevealRaidCardsResultDto } from "./data/responses/raid/RevealRaidCardsResultDto";
import { RevealRaidTargetsResultDto } from "./data/responses/raid/RevealRaidTargetsResultDto";
import { RevealVisitCardsResultDto } from "./data/responses/visit/RevealVisitCardsResultDto";
import { RevealStealCardsResultDto } from "./data/responses/steal/RevealStealCardsResultDto";
import { GoldenWheelClaimRewardResultDto } from "./data/responses/dailywheel/goldenwheel/GoldenWheelClaimRewardResultDto";
import { PiggyClubItemsDto } from "./data/responses/piggyclub/PiggyClubItemsDto";
import { PiggyClubNftsDto } from "./data/responses/piggyclub/PiggyClubNftsDto";
import { TournamentSettingsDto } from "./data/responses/tournament/TournamentSettingsDto";

@injectable()
export class NetworkClient implements INetworkClient {
    public readonly onError: Event<any>;

    private readonly logger: ILogger;
    private readonly queryBaseURL: string;
    private readonly queryApiV2BaseURL: string;
    private readonly variableSaver: VariableSaver;
    private readonly networkConfig: INetworkConfig;
    private httpClient: HttpClientWrapper;
    private readonly guestInfoVCS: PromiseCompletionSource<GuestAuthInfo>;
    private initialized: boolean;
    private refreshTokenProcessStarted: PromiseCompletionSource<void>;

    public constructor(
        @inject("INetworkConfig") networkConfig: INetworkConfig,
        @inject("WaitForGuestInfo") guestInfoVCS: PromiseCompletionSource<GuestAuthInfo>,
        @inject("ILogger") logger: ILogger,
        variableSaver: VariableSaver
    ) {
        this.networkConfig = networkConfig;
        this.variableSaver = variableSaver;
        this.logger = logger;
        this.queryBaseURL = `${this.networkConfig.apiURL}api/${this.networkConfig.apiVersion}`;
        this.queryApiV2BaseURL = `${this.networkConfig.apiURL}api/${this.networkConfig.apiV2Version}`;
        this.guestInfoVCS = guestInfoVCS;
        this.initialized = false;
        this.onError = new Event<any>();
    }

    @initializer()
    public async initialize(): Promise<void> {
        const guestInfo = await this.guestInfoVCS.result;
        if (guestInfo.id == undefined) {
            throw new Error("Failed to create NetworkClient due to guest authentication answer mismatch.");
        }
        this.httpClient = new HttpClientWrapper(axios.create({
            headers: {
                "X-Guest-Id": guestInfo.id,
                "Access-Control-Allow-Credentials": true,
            }
        }), this.logger, [400, 401, 403, 404, 409, 500]);
        if (this.variableSaver.exist(AuthConstants.ACCESS_TOKEN)) {
            this.httpClient.defaults.headers.common["Authorization"] = `Bearer ${this.variableSaver.getString(AuthConstants.ACCESS_TOKEN)}`;
        }
        this.onRequestSuccess = this.onRequestSuccess.bind(this);
        this.onRequestFailure = this.onRequestFailure.bind(this);
        this.httpClient.interceptors.response.use(this.onRequestSuccess, this.onRequestFailure);
        this.initialized = true;
    }

    private async onRequestSuccess(response: AxiosResponse): Promise<AxiosResponse> {
        return response;
    }

    private async onRequestFailure(error: any): Promise<void> {
        const originalRequest = error.config;
        if (error.response?.statuscode === 401 || error.response?.status === 401 && this.variableSaver.exist(AuthConstants.REFRESH_TOKEN)) {
            const refreshTokenString = this.variableSaver.getString(AuthConstants.REFRESH_TOKEN);
            if (refreshTokenString != undefined && refreshTokenString != "" && !originalRequest._retry) {
                originalRequest._retry = true;
                const requestBody = { refreshTokenString: refreshTokenString }
                try {
                    if (this.refreshTokenProcessStarted != null) {
                        await this.refreshTokenProcessStarted.result;
                        originalRequest.headers["Authorization"] = `Bearer ${this.variableSaver.getString(AuthConstants.ACCESS_TOKEN)}`;
                        return this.httpClient.request(originalRequest);
                    }
                    else {
                        this.refreshTokenProcessStarted = new PromiseCompletionSource<void>();
                        const result = await this.refreshToken(requestBody);
                        this.variableSaver.saveString(AuthConstants.ACCESS_TOKEN, result.accessToken);
                        this.variableSaver.saveString(AuthConstants.REFRESH_TOKEN, result.refreshToken);
                        originalRequest.headers["Authorization"] = `Bearer ${result.accessToken}`;
                        this.refreshTokenProcessStarted.complete();
                        this.refreshTokenProcessStarted = null;
                    }
                } catch (e) {
                    this.logger.error("Refresh token error", e);
                    this.onError.emit(error);
                }
                return this.httpClient.request(originalRequest);
            }
        }
        this.onError.emit(error);
        throw error;
    }

    public removeAuthorizationHeader(): void {
        this.httpClient.defaults.headers.common["Authorization"] = undefined;
    }

    public async getCurrentAction(): Promise<AxiosResponse<GetCurrentGuestActionResultDto>> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        return (await this.httpClient.get<GetCurrentGuestActionResultDto, AxiosResponse<GetCurrentGuestActionResultDto>>(this.queryBaseURL + this.networkConfig.currentGuestActionPath));
    }

    public async claimAction(fbEventId?: string): Promise<AxiosResponse<ClaimCurrentActionResultDto>> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        return (await this.httpClient.post<ClaimCurrentActionResultDto, AxiosResponse<ClaimCurrentActionResultDto>>(this.queryBaseURL + this.networkConfig.claimActionPath, {}, { headers: { "X-Fb-Event-Id": fbEventId } }));
    }

    public async getGuestProfile(): Promise<GuestProfileDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const result = (await this.httpClient.get<GuestProfileDto, AxiosResponse<GuestProfileDto>>(this.queryBaseURL + this.networkConfig.guestsMeProfilePath)).data;
        return result;
    }

    public async updateGuestProfile(firstName: string, avatarUrl: string): Promise<GuestProfileDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        return (await this.httpClient.patch<GuestProfileDto>(
            this.queryBaseURL + this.networkConfig.guestsMeProfilePath,
            {
                firstName,
                avatarUrl
            }
        )).data;
    }

    public async getPlayerData(): Promise<PlayerDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const result = (await this.httpClient.get<PlayerDto, AxiosResponse<PlayerDto>>(this.queryBaseURL + this.networkConfig.guestsApiPath + this.networkConfig.guestsMePath)).data;
        this.httpClient.defaults.headers["X-Guest-Id"] = result.id;
        return result;
    }

    public async getGuestBuildings(_limit: number = 10, _offset: number = 0): Promise<GetGuestBuildingsResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        return (await this.httpClient.get<GetGuestBuildingsResultDto, AxiosResponse<GetGuestBuildingsResultDto>>(
            this.queryBaseURL + this.networkConfig.guestBuildingsPath/*TODO egorja: Ask about query params for limit buildings, { params: { limit, offset } }*/)).data;
    }

    public async getBuildingUpgrades(buildingId: string): Promise<BuildingUpgradesDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryApiV2BaseURL}${this.networkConfig.buildingUpgradesPath}/${buildingId}`;
        return (await this.httpClient.get<BuildingUpgradesDto, AxiosResponse<BuildingUpgradesDto>>(queryURL)).data;
    }

    public async upgradeBuilding(buildingId: string, itemId: string, upgradeItemInputBody: UpgradeGuestBuildingItemInputBody): Promise<UpgradeBuildingItemResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryApiV2BaseURL}${this.networkConfig.buildingUpgradesPath}/${buildingId}/items/${itemId}/upgrade`;
        return (await this.httpClient.post<UpgradeBuildingItemResultDto, AxiosResponse<UpgradeBuildingItemResultDto>>(queryURL, upgradeItemInputBody)).data;
    }

    public async fixBuilding(buildingId: string, itemId: string, fixItemInputBody: FixGuestBuildingItemInputBody): Promise<FixBuildingItemResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryApiV2BaseURL}${this.networkConfig.buildingUpgradesPath}/${buildingId}/items/${itemId}/fix`;
        return (await this.httpClient.post<FixBuildingItemResultDto, AxiosResponse<FixBuildingItemResultDto>>(queryURL, fixItemInputBody)).data;
    }

    public async getDailyBonusWheelStatus(): Promise<DailyBonusWheelStatusDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.dailyBonusWheelPath}/status`;
        return (await this.httpClient.post<DailyBonusWheelStatusDto, AxiosResponse<DailyBonusWheelStatusDto>>(queryURL)).data;
    }

    public async claimDailyBonusWheelReward(fbEventId: string): Promise<ClaimDailyWheelRewardResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.dailyBonusWheelPath}${this.networkConfig.claimBonusWheelRewardPath}`;
        return (await this.httpClient.post<ClaimDailyWheelRewardResultDto, AxiosResponse<ClaimDailyWheelRewardResultDto>>(queryURL, {}, { headers: { "X-Fb-Event-Id": fbEventId } })).data;
    }

    public async getGoldenWheelStatus(): Promise<GoldenWheelStatusDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.goldenWheelPath}/status`;
        return (await this.httpClient.post<GoldenWheelStatusDto, AxiosResponse<GoldenWheelStatusDto>>(queryURL)).data;
    }

    public async claimGoldenWheelReward(): Promise<GoldenWheelClaimRewardResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryApiV2BaseURL}${this.networkConfig.goldenWheelPath}${this.networkConfig.claimBonusWheelRewardPath}`;
        return (await this.httpClient.post<GoldenWheelClaimRewardResultDto>(queryURL)).data;
    }

    public async getHourlyBonusStatus(): Promise<GetHourlyBonusesResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.hourlyBonusPath}`;
        return (await this.httpClient.get<GetHourlyBonusesResultDto, AxiosResponse<GetHourlyBonusesResultDto>>(queryURL)).data;
    }

    public async claimHourlyBonusReward(): Promise<ClaimHourlyBonusResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.hourlyBonusPath}${this.networkConfig.claimHourlyBonusPath}`;
        return (await this.httpClient.post<ClaimHourlyBonusResultDto, AxiosResponse<ClaimHourlyBonusResultDto>>(queryURL)).data;
    }

    public async getShopItems(): Promise<GetShopItemsDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.shopPath}`;
        return (await this.httpClient.get<GetShopItemsDto, AxiosResponse<GetShopItemsDto>>(queryURL)).data;
    }

    public async getPersonalOffer(source: PersonalOfferReason): Promise<GetPersonalOfferDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryParams = this.encodeQuery({ source });
        const queryURL = `${this.queryBaseURL}${this.networkConfig.personalOfferPath}${this.networkConfig.personalOfferOutOfRollsPath}${queryParams}`;
        return (await this.httpClient.get<GetPersonalOfferDto>(queryURL)).data;
    }

    public async sendPushSubscription(isNotificationDialogAvailable: boolean, subscription?: PushSubscription, timeout?: boolean): Promise<PlayerDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.guestsApiPath}`;
        let serializedSubscription;
        if (subscription != undefined) {
            serializedSubscription = subscription.toJSON();
        }
        let body: {
            signUpStatus: string,
            pushSubscription?: PushSubscriptionJSON,
            isNotificationDialogAvailable: boolean,
            subscriptionPromiseNotFulfilled?: boolean
        } = {
            signUpStatus: "CONFIRMED_FROM_HOME",
            pushSubscription: serializedSubscription,
            isNotificationDialogAvailable,
            subscriptionPromiseNotFulfilled: timeout ? true : undefined
        };
        return (await this.httpClient.patch<PlayerDto, AxiosResponse<PlayerDto, PushSubscriptionData>, PushSubscriptionData>(
            queryURL,
            body
        )).data;
    }

    public async createBluesnapWallet(bluesnapWalletParams: CreateBluesnapWalletParamsDto): Promise<CreateBluesnapWalletResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.paymentsPath}${this.networkConfig.bluesnapWalletPath}`;
        return (await this.httpClient.post<CreateBluesnapWalletResultDto, AxiosResponse<CreateBluesnapWalletResultDto>>(queryURL, bluesnapWalletParams)).data;
    }

    public async createBlusenapPayment(bluesnapOrderParams: ProcessBluesnapOrderParamsDto): Promise<string> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.paymentsPath}${this.networkConfig.bluesnapPath}`;
        const { data, headers } = await this.httpClient.post<ClaimDailyWheelRewardResultDto>(queryURL, bluesnapOrderParams);
        return headers['x-fb-event-id'];
    }

    public async createBlusnapCreditCards(bluesnapCreditCardsParams: ProcessBluesnapCreditCardOrderDto): Promise<void> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.paymentsPath}${this.networkConfig.bluesnapCreditCardPath}`;
        await this.httpClient.post<ClaimDailyWheelRewardResultDto>(queryURL, bluesnapCreditCardsParams);
    }

    public async addBuildingToMarketplace(buildingData: AddBuildingToMarketplaceDto): Promise<AddBuildingToMarketplaceResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.marketplaceBuildingsPath}`;
        return (await this.httpClient.post<AddBuildingToMarketplaceResultDto, AxiosResponse<AddBuildingToMarketplaceResultDto>, AddBuildingToMarketplaceDto>(queryURL, buildingData)).data;
    }

    public async editMarketplaceBuildingPrice(mpBuildingId: string, newPrice: EditBuildingMarketlacePriceDto): Promise<void> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.marketplaceBuildingsPath}/${mpBuildingId}`;
        const result = await this.httpClient.patch<void>(queryURL, newPrice);
        if (result.status != 200) {
            throw new Error(`Failed to update marketplace building price.\nStatus code: ${result.status}\nInfo: ${result.data}\nPlease contact support for additional info.\n`);
        }
    }

    public async deleteBuildingFromMarketplace(mpBuildingId: string): Promise<void> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.marketplaceBuildingsPath}/${mpBuildingId}`;
        const result = await this.httpClient.delete<void>(queryURL);
        if (result.status != 204) {
            throw new Error(`Failed to remove building from marketplace.\nStatus code: ${result.status}\nInfo: ${result.data}\nPlease contact support for additional info.`);
        }
    }

    public async getMarketplaceBuildings(filter: GetMarketplaceBuildingsDto): Promise<GetMarketplaceBuildingsResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryParams = this.encodeQuery(filter);
        const queryURL = `${this.queryBaseURL}${this.networkConfig.marketplaceBuildingsPath}${queryParams}`;
        return (await this.httpClient.get<GetMarketplaceBuildingsResultDto>(queryURL)).data;
    }

    public async getMarketplaceBuildingFullInfo(mpBuildingId: string): Promise<MarketplaceBuildingItemDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.marketplaceBuildingsPath}/${mpBuildingId}`;
        return (await this.httpClient.get<MarketplaceBuildingItemDto>(queryURL))?.data ?? null;
    }

    public async getMarketplaceMapAreas(returnOnlyBuildingsByMon: boolean = false): Promise<MarketplaceMapPointsDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryParams = this.encodeQuery({ returnOnlyBuildingsByMon });
        const queryURL = `${this.queryBaseURL}${this.networkConfig.merketplaceMapPointsPath}${queryParams}`;
        return (await this.httpClient.get<MarketplaceMapPointsDto>(queryURL)).data;
    }

    public async getMarketplaceZipCodes(area: number, returnOnlyBuildingsByMon: boolean = false): Promise<MarketplaceMapPointsDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryParams = this.encodeQuery({ area, returnOnlyBuildingsByMon });
        const queryURL = `${this.queryBaseURL}${this.networkConfig.merketplaceMapPointsPath}${queryParams}`;
        return (await this.httpClient.get<MarketplaceMapPointsDto>(queryURL)).data;
    }

    public async getMarketplaceNews(): Promise<MarketplaceNewsDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.marketplaceNewsPath}`;
        return (await this.httpClient.get<MarketplaceNewsDto>(queryURL)).data;
    }

    public async refreshToken(refreshTokenInput: RefreshTokenInput): Promise<AuthTokensDto> {
        this.throwIfNotInitialized();
        try {
            const queryURL = `${this.networkConfig.apiURL}api${this.networkConfig.authPath}${this.networkConfig.refreshTokenPath}`;
            const result = (await this.httpClient.post<AuthTokensDto>(queryURL, refreshTokenInput)).data;
            this.httpClient.defaults.headers.common["Authorization"] = `Bearer ${result.accessToken}`;
            return result;
        }
        catch (error: any) {
            this.logger.debug(`Exception on refresh token: ${error?.message ?? error}`);
            return { accessToken: AuthTokenDisconnectionState, refreshToken: "" };
        }
    }

    public async getFirstTimeExperience(): Promise<FirstTimeExperienceDto> {
        this.throwIfNotInitialized();
        const queryURL = `${this.networkConfig.apiURL}api${this.networkConfig.firstTimeExperiencePath}`;
        return (await this.httpClient.get<FirstTimeExperienceDto>(queryURL)).data;
    }

    public async editFirstTimeExperience(body: UpdateFirstTimeExperienceInputBody): Promise<UpdateFirstTimeExperienceInputBody> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.networkConfig.apiURL}api${this.networkConfig.firstTimeExperiencePath}`;
        return (await this.httpClient.patch<UpdateFirstTimeExperienceInputBody>(queryURL, body)).data;
    }

    public async getRenterPolicyStatus(): Promise<RenterPolicyStatusDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.networkConfig.apiURL}api/${this.networkConfig.apiVersion}${this.networkConfig.renterPolicyPath}/status`;
        return (await this.httpClient.post<RenterPolicyStatusDto>(queryURL)).data;
    }

    public async getLeaderboardStatus(): Promise<LeaderboardStatusDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.networkConfig.apiURL}api/${this.networkConfig.apiVersion}${this.networkConfig.leaderboardPath}/status`;
        return (await this.httpClient.post<LeaderboardStatusDto>(queryURL)).data;
    }

    public async leaderboardClaim(leaderboardId: string): Promise<LeaderboardClaimResonseDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.networkConfig.apiURL}api/${this.networkConfig.apiVersion}${this.networkConfig.leaderboardPath}/${leaderboardId}/claim`;
        return (await this.httpClient.post<LeaderboardClaimResonseDto>(queryURL)).data;
    }

    public async latestNewsStatus(): Promise<LatestNewsStatusDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.networkConfig.apiURL}api/${this.networkConfig.apiVersion}${this.networkConfig.latestNews}/status`;
        return (await this.httpClient.get<LatestNewsStatusDto>(queryURL)).data;
    }

    public async getLatestNews(setAsRead?: boolean): Promise<LatestNewsDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.networkConfig.apiURL}api/${this.networkConfig.apiVersion}${this.networkConfig.latestNews}${setAsRead != null ? '?setAsRead=' + setAsRead : ''}`;
        return (await this.httpClient.get<LatestNewsDto>(queryURL)).data;
    }

    public async getLatestNewsInfoFromId(latestNewsId: string): Promise<LatestNewsExpandedPopupInfoDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.networkConfig.apiURL}api/${this.networkConfig.apiVersion}${this.networkConfig.latestNews}/${latestNewsId}`;
        return (await this.httpClient.get<LatestNewsExpandedPopupInfoDto>(queryURL)).data;
    }

    public async getPiggyBankStatus(): Promise<PiggyBankDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.networkConfig.apiURL}api/${this.networkConfig.apiVersion}${this.networkConfig.piggyBankPath}`;
        return (await this.httpClient.get<PiggyBankDto>(queryURL)).data;
    }

    public async getBenefits(): Promise<GuestBenefitsDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.networkConfig.apiURL}api/${this.networkConfig.apiVersion}${this.networkConfig.benefitsPath}`;
        return (await this.httpClient.get<GuestBenefitsDto>(queryURL)).data;
    }

    public async getPiggyWheelStatus(): Promise<PiggyWheelStatusDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.piggyWheelPath}/status`;
        return (await this.httpClient.post<PiggyWheelStatusDto, AxiosResponse<PiggyWheelStatusDto>>(queryURL)).data;
    }

    public async claimPiggyWheelReward(): Promise<ClaimPiggyWheelRewardResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.piggyWheelPath}${this.networkConfig.claimBonusWheelRewardPath}`;
        return (await this.httpClient.post<ClaimPiggyWheelRewardResultDto, AxiosResponse<ClaimPiggyWheelRewardResultDto>>(queryURL)).data;
    }

    public async authWeb3Nonce(guestId: string, walletAddress: string): Promise<AuthNonceDto> {
        this.throwIfNotInitialized();
        const queryUrl = `${this.networkConfig.apiURL}api${this.networkConfig.web3Auth}/nonce`;
        const body = {
            guestId,
            walletAddress
        };
        return (await this.httpClient.post(queryUrl, body)).data;
    }

    public async authWeb3(walletAddress: string, signature: string, gameVersion: string): Promise<AuthTokensDto> {
        this.throwIfNotInitialized();
        const queryUrl = `${this.networkConfig.apiURL}api${this.networkConfig.web3Auth}`;
        const body: any = {
            walletAddress,
            signature,
            resolution: `${Math.round(window.innerWidth * window.devicePixelRatio)}x${Math.round(window.innerHeight * window.devicePixelRatio)}`,
            timezone: Intl.DateTimeFormat()?.resolvedOptions()?.timeZone,
            languages: navigator.languages,
            gameVersion
        };
        const result = (await this.httpClient.post(queryUrl, body)).data;
        this.httpClient.defaults.headers.common["Authorization"] = `Bearer ${result.accessToken}`;
        return result;
    }

    public async guestBuildingPrepareMint(buildingId: string): Promise<PrepareMintResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.guestBuildingsPath}/${buildingId}/${this.networkConfig.prepareMintPath}`;
        return (await this.httpClient.post<PrepareMintResultDto, AxiosResponse<PrepareMintResultDto>>(queryURL)).data;
    }

    public async mpPrepareMint(mpBuildingId: string, couponBenefitIds?: Array<string>): Promise<PrepareMintResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        let body = { couponBenefitIds };
        const queryURL = `${this.queryBaseURL}${this.networkConfig.marketplaceBuildingsPath}/${mpBuildingId}/${this.networkConfig.prepareMintPath}`;
        return (await this.httpClient.post<PrepareMintResultDto>(queryURL, body)).data;
    }

    public async successPurchaseBuilding(transactionHash: string, tokenId: string): Promise<void> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.successPurchaseBuildingPath}`;
        await this.httpClient.post<void>(queryURL, { transactionHash, tokenId });
    }

    public async successPurchaseStorePackageOnL1(transactionHash: string): Promise<void> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.successPurchaseStorePackageOnL1Path}`;
        await this.httpClient.post<void>(queryURL, { transactionHash });
    }

    public async successPurchaseStorePackageOnBase(transactionHash: string): Promise<void> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.successPurchaseStorePackageOnBasePath}`;
        await this.httpClient.post<void>(queryURL, { transactionHash });
    }

    public async storePackageBurn(transactionHash: string): Promise<BurnNFTResultDTO> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.successStoreBurnPath}`;
        return (await this.httpClient.post<BurnNFTResultDTO>(queryURL, { transactionHash })).data;
    }

    public async hourlyBonusBurn(transactionHash: string): Promise<RewardDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.hourlyBonusPath}${this.networkConfig.hourlyBonusBurnPath}`;
        return (await this.httpClient.post<RewardDto>(queryURL, { transactionHash })).data;
    }

    public async dailyWheelBurn(transactionHash: string): Promise<RewardDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.dailyBonusWheelPath}${this.networkConfig.web3BurnStatusPath}`;
        return (await this.httpClient.post<RewardDto>(queryURL, { transactionHash })).data;
    }

    public async goldenWheelBurn(transactionHash: string): Promise<RewardDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.goldenWheelPath}${this.networkConfig.web3BurnStatusPath}`;
        return (await this.httpClient.post<RewardDto>(queryURL, { transactionHash })).data;
    }

    public async personalOfferBurn(transactionHash: string): Promise<PersonalOfferBurnResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.queryBaseURL}${this.networkConfig.personalOfferPath}${this.networkConfig.web3BurnStatusPath}`;
        return (await this.httpClient.post<PersonalOfferBurnResultDto>(queryURL, { transactionHash })).data;
    }

    public async linkExternalWallet(walletAddress: string, signature: string, type: string): Promise<PlayerDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.networkConfig.apiURL}api${this.networkConfig.web3Auth}/link-external-wallet`;
        return (await this.httpClient.post<PlayerDto>(queryURL, { walletAddress, signature, type })).data;
    }

    public async unlinkExternalWallet(walletAddress: string): Promise<UnklinkWalletDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.networkConfig.apiURL}api${this.networkConfig.web3Auth}/unlink-external-wallet`;
        return (await this.httpClient.post<UnklinkWalletDto>(queryURL, { walletAddress })).data;
    }

    public async getExternalWallets(): Promise<WalletsDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.networkConfig.apiURL}api${this.networkConfig.web3Auth}/external-wallet`;
        return (await this.httpClient.get<WalletsDto>(queryURL)).data;
    }

    public async claimGiftReward(): Promise<RevealGiftCardResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        return (await this.httpClient.post<RevealGiftCardResultDto, AxiosResponse<RevealGiftCardResultDto>>(this.queryBaseURL + this.networkConfig.giftActionCardsPath)).data;
    }

    public async getRaidActionCards(): Promise<RevealRaidCardsResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        return (await this.httpClient.post<RevealRaidCardsResultDto, AxiosResponse<RevealRaidCardsResultDto>>(this.queryBaseURL + this.networkConfig.raidActionCardsPath)).data;
    }

    public async getRaidActionTargets(): Promise<RevealRaidTargetsResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        return (await this.httpClient.post<RevealRaidTargetsResultDto, AxiosResponse<RevealRaidTargetsResultDto>>(this.queryBaseURL + this.networkConfig.raidActionTargetsPath)).data;
    }

    public async getVisitActionCards(): Promise<RevealVisitCardsResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        return (await this.httpClient.post<RevealVisitCardsResultDto, AxiosResponse<RevealVisitCardsResultDto>>(this.queryBaseURL + this.networkConfig.visitActionCardsPath)).data;
    }

    public async getStealActionCards(): Promise<RevealStealCardsResultDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        return (await this.httpClient.post<RevealStealCardsResultDto, AxiosResponse<RevealStealCardsResultDto>>(this.queryBaseURL + this.networkConfig.stealActionCardsPath)).data;
    }

    public async getPiggyClubs(): Promise<PiggyClubItemsDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.networkConfig.apiURL}api/${this.networkConfig.apiVersion}${this.networkConfig.piggyClubs}`;
        return (await this.httpClient.get<PiggyClubItemsDto>(queryURL)).data;
    }

    public async getPiggyClubNFTs(): Promise<PiggyClubNftsDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.networkConfig.apiURL}api/${this.networkConfig.apiVersion}${this.networkConfig.piggyClubs}/guest-nfts`;
        return (await this.httpClient.get<PiggyClubNftsDto>(queryURL)).data;
    }

    public async getTournamentsSettings(): Promise<TournamentSettingsDto> {
        this.throwIfNotInitialized();
        await this.waitForTokenRefresh();
        const queryURL = `${this.networkConfig.apiURL}api/${this.networkConfig.apiVersion}${this.networkConfig.tournamentsSettings}`;
        return (await this.httpClient.get<TournamentSettingsDto>(queryURL)).data;
    }

    private throwIfNotInitialized() {
        if (!this.initialized) {
            throw new Error("You should call initialize method for NetworkClient to work properly.");
        }
    }

    private async waitForTokenRefresh(): Promise<void> {
        return this.refreshTokenProcessStarted != null ? this.refreshTokenProcessStarted.result : Promise.resolve();
    }

    private encodeQuery<T>(data: T): string {
        let query = "?";
        for (const prop in data) {
            if (data.hasOwnProperty(prop)) {
                if (data[prop] != undefined) {
                    query += `${prop}=${data[prop]}&`;
                }
            }
        }
        query = query.substring(0, query.length - 1);
        return query;
    }
}
